import type { ReactNode } from 'react'
import { Reveal } from './Reveal'
import styles from './SplitSection.module.css'

interface SplitSectionProps {
  id: string
  eyebrow: string
  title: ReactNode
  /** Body copy: one or more <p> elements. */
  children: ReactNode
  /** Usually a <MediaFrame>. */
  media: ReactNode
  /** Put the media on the left. Default places it on the right. */
  mediaLeft?: boolean
  /** Stack slightly earlier when real UI detail needs a wider media column. */
  stackEarly?: boolean
  /** Short closing line set apart from the body copy. */
  footnote?: string
}

/**
 * Editorial media / copy split. The media column is deliberately much wider
 * than the text measure so the composition stays asymmetric rather than
 * reading as two equal halves. Stacks to copy-then-media below 940px.
 */
export function SplitSection({
  id,
  eyebrow,
  title,
  children,
  media,
  mediaLeft = false,
  stackEarly = false,
  footnote,
}: SplitSectionProps) {
  return (
    <section id={id} className={styles.section}>
      <div
        className={`container container--wide ${styles.grid} ${
          mediaLeft ? styles.mediaLeft : ''
        } ${stackEarly ? styles.stackEarly : ''}`}
      >
        <Reveal className={styles.copyCol}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h2 className={styles.title}>{title}</h2>
          <div className={styles.body}>{children}</div>
          {footnote ? <p className={styles.footnote}>{footnote}</p> : null}
        </Reveal>

        <Reveal className={styles.mediaCol} delayMs={90}>
          {media}
        </Reveal>
      </div>
    </section>
  )
}
